import {
    type TutorBotChatUnlockedConfig,
    getUnavailableConfig,
    useTutorBotLockedConfig,
    type TutorBotChatStatusConfig,
    UnlockedTutorBotChatState,
} from 'TutorBotChat';
import { useNavigationHelper, useCurrentUser, useTargetBrandConfig } from 'FrontRoyalAngular';
import { type CurrentUser, getAdmissionOffer, getAoiRecord, getCohort, getPreferredName } from 'Users';
import { getApplicationDeadline, getStartDate } from 'Cohorts';
import { AoiRecordType } from 'ProgramAoi';
import { getRegistrationDeadline } from 'AdmissionOffer';
import { useErrorLogService } from 'ErrorLogging';
import { type DateHelper } from 'DateHelper';
import { angularInjectorProvider } from 'Injector';
import { type BrandConfig } from 'AppBranding';
import { determineBotPageState } from './determineBotPageState';
import './locales/ai_advisor_chat/ai_advisor_chat-en.json';
import { AI_ADVISOR_BRAND_NAME } from './constants';

function getUnlockedPartialConfig({ brandConfig, botName }: { brandConfig: BrandConfig; botName: string }) {
    return {
        isLocked: false,
        inputLabel: { key: 'ai_advisor_chat.ai_advisor_chat.how_can_i_help' },
        welcomeModalTexts: [
            {
                key: 'ai_advisor_chat.ai_advisor_chat.welcome_message',
                params: { institutionName: brandConfig.brandNameShort, faqUrl: brandConfig.tutorBotFaqUrl, botName },
            },
            { key: 'chat.chat.ai_welcome_message_stay_tuned' },
        ],
    } as const;
}

function getUnlockedIncludedOrGraduatedConfig({
    currentUser,
    name,
    brandConfig,
    botName,
}: {
    currentUser: CurrentUser;
    name: string;
    brandConfig: BrandConfig;
    botName: string;
}): TutorBotChatUnlockedConfig {
    // has_seen_ai_welcome_message is for intended for a different purpose, but we piggyback on it to determine
    // whether to show "Nice to meet you" or "Welcome back" the first time an included/graduated user visits the bot page.
    const hasSeenAiWelcomeMessage = !!currentUser?.hasSeenAiWelcomeMessage;
    const introductoryMessageKey = hasSeenAiWelcomeMessage
        ? 'ai_advisor_chat.greeting_message.includedOrGraduatedWelcomeBack'
        : 'ai_advisor_chat.greeting_message.includedOrGraduatedNiceToMeetYou';

    return {
        ...getUnlockedPartialConfig({ brandConfig, botName }),
        greetingMessages: [
            { key: introductoryMessageKey, params: { name } },
            { key: 'ai_advisor_chat.greeting_message.includedOrGraduatedImHere' },
            { key: 'ai_advisor_chat.greeting_message.includedOrGraduatedLearningAndImproving' },
            { key: 'ai_advisor_chat.greeting_message.includedOrGraduatedHowCanIHelp' },
        ],
        // This value needs to stay the same as what we were historically using prior to enabling AI Advisor
        // access for prospective students, so that existing users will not see a new greeting message.
        hasSeenInitialGreetingLocalStorageKey: 'hasSeenInitialGreeting',
    };
}

function getUnlockedCanApplyConfig({
    dateHelper,
    navigationHelper,
    currentUser,
    name,
    brandConfig,
    botName,
}: {
    dateHelper: DateHelper;
    navigationHelper: ReturnType<typeof useNavigationHelper>;
    currentUser: CurrentUser;
    name: string;
    brandConfig: BrandConfig;
    botName: string;
}): TutorBotChatUnlockedConfig {
    // CanApply is the catch-all state for prospective users, who may have no cohort, so guard the
    // null case rather than asserting. formattedUserFacingMonthDayLong returns '' for a null date.
    const cohort = getCohort(currentUser);
    const applicationDeadline = dateHelper.formattedUserFacingMonthDayLong(
        (cohort ? getApplicationDeadline(cohort) : null) as Date,
        true,
    );

    return {
        ...getUnlockedPartialConfig({ brandConfig, botName }),
        welcomeModalCta: {
            message: {
                headline: { key: 'ai_advisor_chat.ai_advisor_chat.locked_welcome_modal_cta_message_signup_headline' },
                subtext: {
                    key: 'ai_advisor_chat.ai_advisor_chat.locked_welcome_modal_cta_message_signup_subtext',
                    params: { applicationDeadline },
                },
            },
            buttonText: { key: 'ai_advisor_chat.ai_advisor_chat.locked_welcome_modal_cta_button_text_signup' },
            onClick: () => navigationHelper.loadRoute('/settings/application'),
        },
        greetingMessages: [
            { key: 'ai_advisor_chat.greeting_message.signupHiName', params: { name } },
            {
                key: 'ai_advisor_chat.greeting_message.signupMyMission',
                params: { institutionName: brandConfig.brandNameShort },
            },
            {
                key: 'ai_advisor_chat.greeting_message.signupHappyToHelp',
            },
        ],
        hasSeenInitialGreetingLocalStorageKey: null,
    };
}

function getUnlockedCannotApplyConfig({
    name,
    brandConfig,
    botName,
}: {
    name: string;
    brandConfig: BrandConfig;
    botName: string;
}): TutorBotChatUnlockedConfig {
    return {
        ...getUnlockedPartialConfig({ brandConfig, botName }),
        greetingMessages: [
            { key: 'ai_advisor_chat.greeting_message.signupHiName', params: { name } },
            {
                key: 'ai_advisor_chat.greeting_message.signupMyMission',
                params: { institutionName: brandConfig.brandNameShort },
            },
            {
                key: 'ai_advisor_chat.greeting_message.signupHappyToHelp',
            },
        ],
        hasSeenInitialGreetingLocalStorageKey: null,
    };
}

function getUnlockedAppliedConfig({
    name,
    brandConfig,
    botName,
}: {
    name: string;
    brandConfig: BrandConfig;
    botName: string;
}): TutorBotChatUnlockedConfig {
    return {
        ...getUnlockedPartialConfig({ brandConfig, botName }),
        greetingMessages: [
            { key: 'ai_advisor_chat.greeting_message.appliedHiName', params: { name } },
            {
                key: 'ai_advisor_chat.greeting_message.appliedImHere',
            },
        ],
        hasSeenInitialGreetingLocalStorageKey: null,
    };
}

function getUnlockedAppliedInterviewPendingConfig({
    name,
    brandConfig,
    botName,
    currentUser,
    navigationHelper,
}: {
    name: string;
    brandConfig: BrandConfig;
    botName: string;
    currentUser: CurrentUser;
    navigationHelper: ReturnType<typeof useNavigationHelper>;
}): TutorBotChatUnlockedConfig {
    const programApplication = getAoiRecord(currentUser, AoiRecordType.ProgramApplication);
    return {
        // same introductory messages, but new CTA
        ...getUnlockedAppliedConfig({ name, brandConfig, botName }),
        welcomeModalCta: {
            message: {
                headline: {
                    key: 'ai_advisor_chat.ai_advisor_chat.locked_welcome_modal_cta_message_applied_interview_pending_headline',
                },
            },
            buttonText: {
                key: 'ai_advisor_chat.ai_advisor_chat.locked_welcome_modal_cta_button_text_applied_interview_pending',
            },
            onClick: () => navigationHelper.loadUrl(programApplication!.invitedToInterviewUrl),
        },
    };
}

function getUnlockedNotOfferedAdmissionConfig({
    name,
    brandConfig,
    botName,
}: {
    name: string;
    brandConfig: BrandConfig;
    botName: string;
}): TutorBotChatUnlockedConfig {
    return {
        ...getUnlockedPartialConfig({ brandConfig, botName }),
        greetingMessages: [
            { key: 'ai_advisor_chat.greeting_message.notOfferedAdmissionHiName', params: { name } },
            {
                key: 'ai_advisor_chat.greeting_message.notOfferedAdmissionMyMission',
                params: { institutionName: brandConfig.brandNameShort },
            },
        ],
        hasSeenInitialGreetingLocalStorageKey: null,
    };
}

function getUnlockedOfferedAdmissionConfig({
    dateHelper,
    navigationHelper,
    currentUser,
    name,
    brandConfig,
    botName,
}: {
    dateHelper: DateHelper;
    navigationHelper: ReturnType<typeof useNavigationHelper>;
    currentUser: CurrentUser;
    name: string;
    brandConfig: BrandConfig;
    botName: string;
}): TutorBotChatUnlockedConfig {
    const cohort = getCohort(currentUser)!;
    const admissionOffer = getAdmissionOffer(currentUser);
    const unformattedRegistrationDeadline = admissionOffer && getRegistrationDeadline(admissionOffer, cohort);
    const registrationDeadline =
        unformattedRegistrationDeadline && dateHelper.formattedUserFacingMonthDayLong(unformattedRegistrationDeadline);

    return {
        ...getUnlockedPartialConfig({ brandConfig, botName }),
        welcomeModalCta: {
            message: {
                headline: {
                    key: 'ai_advisor_chat.ai_advisor_chat.locked_welcome_modal_cta_message_offered_admission_headline',
                },
                subtext: {
                    key: 'ai_advisor_chat.ai_advisor_chat.locked_welcome_modal_cta_message_offered_admission_subtext',
                    params: { registrationDeadline },
                },
            },
            buttonText: {
                key: 'ai_advisor_chat.ai_advisor_chat.locked_welcome_modal_cta_button_text_offered_admission',
            },
            onClick: () => navigationHelper.loadRoute('/settings/application_status'),
        },
        greetingMessages: [
            { key: 'ai_advisor_chat.greeting_message.offeredAdmissionHiName', params: { name } },
            {
                key: 'ai_advisor_chat.greeting_message.offeredAdmissionExcitingNews',
                params: { institutionName: brandConfig.brandNameShort },
            },
        ],
        hasSeenInitialGreetingLocalStorageKey: null,
    };
}

function getUnlockedOfferExpiredConfig({
    name,
    brandConfig,
    botName,
}: {
    name: string;
    brandConfig: BrandConfig;
    botName: string;
}): TutorBotChatUnlockedConfig {
    return {
        ...getUnlockedPartialConfig({ brandConfig, botName }),
        greetingMessages: [
            { key: 'ai_advisor_chat.greeting_message.offerExpiredHiName', params: { name } },
            { key: 'ai_advisor_chat.greeting_message.offerExpiredImHere' },
        ],
        hasSeenInitialGreetingLocalStorageKey: null,
    };
}

function getUnlockedRegisteredNotCurrentConfig({
    dateHelper,
    currentUser,
    name,
    brandConfig,
    botName,
}: {
    dateHelper: DateHelper;
    currentUser: CurrentUser;
    name: string;
    brandConfig: BrandConfig;
    botName: string;
}): TutorBotChatUnlockedConfig {
    const cohort = getCohort(currentUser)!;
    const startDate = dateHelper.formattedUserFacingMonthDayLong(getStartDate(cohort), false);
    return {
        ...getUnlockedPartialConfig({ brandConfig, botName }),
        greetingMessages: [
            { key: 'ai_advisor_chat.greeting_message.registeredNotCurrentCongratulations', params: { name } },
            {
                key: 'ai_advisor_chat.greeting_message.registeredNotCurrentClassesStartOn',
                params: { startDate },
            },
        ],
        hasSeenInitialGreetingLocalStorageKey: null,
    };
}

function getUnlockedExpelledOrWithdrawnConfig({
    name,
    brandConfig,
    botName,
}: {
    name: string;
    brandConfig: BrandConfig;
    botName: string;
}): TutorBotChatUnlockedConfig {
    return {
        ...getUnlockedPartialConfig({ brandConfig, botName }),
        greetingMessages: [
            { key: 'ai_advisor_chat.greeting_message.didNotGraduateHiName', params: { name } },
            { key: 'ai_advisor_chat.greeting_message.didNotGraduateIKnowThings' },
        ],
        hasSeenInitialGreetingLocalStorageKey: null,
    };
}

// oxlint-disable-next-line max-lines-per-function
export function useBotPageStatusConfig(): TutorBotChatStatusConfig {
    const botName = AI_ADVISOR_BRAND_NAME;
    const tutorBotLockedConfig = useTutorBotLockedConfig({ botName });

    const currentUser = useCurrentUser();
    const navigationHelper = useNavigationHelper();
    const brandConfig = useTargetBrandConfig();
    const ErrorLogService = useErrorLogService();

    // If tutor bot is locked globally, then we just return that config
    if (tutorBotLockedConfig) return tutorBotLockedConfig;

    if (!currentUser) {
        ErrorLogService.notifyInProd(new Error('Expected to have a user when determining bot page state'), null);
        return getUnavailableConfig({ botName });
    }

    const state = determineBotPageState(currentUser);

    const dateHelper = angularInjectorProvider.get<DateHelper>('dateHelper');
    const name = getPreferredName(currentUser);

    switch (state) {
        case UnlockedTutorBotChatState.IncludedOrGraduated:
            return getUnlockedIncludedOrGraduatedConfig({ currentUser, name, brandConfig, botName });
        case UnlockedTutorBotChatState.CanApply:
            return getUnlockedCanApplyConfig({ dateHelper, navigationHelper, currentUser, name, brandConfig, botName });
        case UnlockedTutorBotChatState.CannotApply:
            return getUnlockedCannotApplyConfig({ name, brandConfig, botName });
        case UnlockedTutorBotChatState.Applied:
            return getUnlockedAppliedConfig({ name, brandConfig, botName });
        case UnlockedTutorBotChatState.AppliedInterviewPending:
            return getUnlockedAppliedInterviewPendingConfig({
                name,
                brandConfig,
                botName,
                currentUser,
                navigationHelper,
            });
        case UnlockedTutorBotChatState.NotOfferedAdmission:
            return getUnlockedNotOfferedAdmissionConfig({ name, brandConfig, botName });
        case UnlockedTutorBotChatState.OfferedAdmission:
            return getUnlockedOfferedAdmissionConfig({
                dateHelper,
                navigationHelper,
                currentUser,
                name,
                brandConfig,
                botName,
            });
        case UnlockedTutorBotChatState.OfferExpired:
            return getUnlockedOfferExpiredConfig({ name, brandConfig, botName });
        case UnlockedTutorBotChatState.RegisteredNotCurrent:
            return getUnlockedRegisteredNotCurrentConfig({ dateHelper, currentUser, name, brandConfig, botName });
        case UnlockedTutorBotChatState.ExpelledOrWithdrawn:
            return getUnlockedExpelledOrWithdrawnConfig({ name, brandConfig, botName });
        default:
            ErrorLogService.notifyInProd(new Error(`Unexpected TutorBotState "${state}"`), null);
            return getUnavailableConfig({ botName });
    }
}
